import { api } from './api';

export type BillingMode = 'live' | 'test' | 'off';
export type PlanId = 'monitor' | 'standard' | 'pro';

export interface Plan {
  id: PlanId;
  name: string;
  amount_cents: number;
  interval: string | null;
  highlights: string[];
}

export interface PlansResponse {
  mode: BillingMode;
  checkout_available: boolean;
  plans: Plan[];
}

export interface Subscription {
  plan: PlanId;
  status: string;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  livemode: boolean;
  has_customer: boolean;
  updated_at: string | null;
}

const BASE = '/api/v1/billing';

export const billingService = {
  async plans(): Promise<PlansResponse> {
    const { data } = await api.get(`${BASE}/plans`);
    return data;
  },
  async subscription(): Promise<Subscription> {
    const { data } = await api.get(`${BASE}/subscription`);
    return data;
  },
  async checkout(plan: Exclude<PlanId, 'monitor'>): Promise<string> {
    const { data } = await api.post(`${BASE}/checkout`, { plan });
    return data.url;
  },
  async confirm(sessionId: string): Promise<Subscription> {
    const { data } = await api.post(`${BASE}/checkout/confirm`, { session_id: sessionId });
    return data;
  },
  async portal(): Promise<string> {
    const { data } = await api.post(`${BASE}/portal`);
    return data.url;
  },
};

export const errorMessage = (err: any, fallback: string): string => {
  const detail = err?.response?.data?.detail;
  return typeof detail === 'string' && detail ? detail : fallback;
};

// One shared lookup of the account's plan for prompts across the app; reset after a plan change.
let planCache: Promise<PlanId> | null = null;
let planCacheToken: string | null = null;
export const currentPlan = (): Promise<PlanId> => {
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
  if (token !== planCacheToken) { planCache = null; planCacheToken = token; }
  if (!planCache) {
    planCache = billingService.subscription().then((s) => s.plan).catch((err) => {
      planCache = null;
      throw err;
    });
  }
  return planCache;
};
export const resetPlanCache = () => { planCache = null; };
export const isPaidPlan = (plan: PlanId | null | undefined) => plan === 'standard' || plan === 'pro';
